import { Group, Input, Loader, Radio as R } from '@mantine/core'
import { useForm } from '@mantine/form'
import { ErrorBoundary } from '@sentry/react'
import { openTrapConfigurationModal } from 'App/ServiceCenter/NodeInstallation/TrapConfiguration/TrapConfiguration'
import { LuresMultiSelect } from 'App/ServiceCenter/Shared/LuresMultiSelect'
import { nodeConsumableActions } from 'App/ServiceCenter/utils/constants/nodeConsumables'
import { AboveAllModal } from 'components/AboveAllModalOverlay/AboveAllModalOverlay'
import { FIXED_CHILDREN_MODAL_TRANSITION } from 'components/AboveAllModalOverlay/fixedChildrenModalTransition'
import { EditButton } from 'components/EditButton'
import { WideHeader } from 'components/ModalDrawer/WideHeader/WideHeader'
import { Radio } from 'components/Radio/Radio'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import moment from 'moment-timezone'
import React, { useEffect, useMemo, useState } from 'react'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import { captureFrontendError } from 'utils/captureFrontendError'
import { checkAuthorization } from 'utils/checkAuthorization'
import { showNotification } from 'utils/showNotification'
import { useTimezoneForSelectedPropertyOrRegion } from '../../Map/PanelDetails/_utils/useTimezoneForSelectedPropertyOrRegion'
import { Footer } from '../NodeInstallation/Footer/Footer'
import { networkStore } from '../store/networkStore'
import { serviceCenterStore } from '../store/serviceCenterStore'
import type { TActiveNode, TNodeConsumablesStatus, TNodeLures, TNodeTrap } from '../types'
import { ConsumableType } from '../types'
import { serviceCenterNodeConsumablesStatusGet } from '../utils/api/serviceCenterNodeConsumablesStatusGet'
import { getTrapConsumableLabel } from '../utils/getTrapConsumableLabel'
import { updateNodeConfig } from '../utils/updateNodeConfig'

const MODAL_ID = 'node-consumable'

type NodeConsumablesStatus = {
  lureIdsActive?: number[]
  lureActiveStartDate: string | null
  linerActiveStartDate: string | null
  coroplastActiveStartDate: string | null
  batteryActiveStartDate: string | null
}

type FormValues = {
  lureAction: keyof typeof nodeConsumableActions | null
  linerAction: keyof typeof nodeConsumableActions | null
  coroplastAction: keyof typeof nodeConsumableActions | null
  batteryAction: keyof typeof nodeConsumableActions | null
  lureIdsActive: number[]
}

export const checkIsTrapConsumableUpdatable = (node: TActiveNode) => {
  return (
    checkAuthorization({
      permission: 'EDIT_SSC_UPDATE_NODE_CONFIG',
      entity: (node as TActiveNode).propertyId,
    }) && checkAuthorization({ permission: 'VIEW_SSC_LURES', entity: 'FOR_ANY_ENTITY' })
  )
}

const ClearButton = ({ onClick }: { onClick: () => void }) => (
  <div
    css={{
      textDecoration: 'underline',
      padding: '0 5px',
      marginLeft: 'auto',
      fontSize: 14,
      cursor: 'pointer',
    }}
    onClick={onClick}
  >
    {translate.phrases.banyanApp('Clear')}
  </div>
)

export const openNodeConsumableModal = (node?: TActiveNode) => {
  AboveAllModal.open({
    modalId: MODAL_ID,
    fullScreen: true,
    withCloseButton: false,
    padding: 0,
    transitionProps: FIXED_CHILDREN_MODAL_TRANSITION,
    children: (
      <ErrorBoundary>
        <NodeConsumableModal selectedNode={node} />
      </ErrorBoundary>
    ),
    styles: {
      content: {
        marginLeft: 'env(safe-area-inset-left)',
        marginRight: 'env(safe-area-inset-right)',
        boxShadow: 'none',
      },
    },
  })
}

const ConsumableUpdatedLabel: React.FC<{
  date?: string | null
  timezone: string
}> = ({ date, timezone }) => {
  if (!date) return null

  const formattedDate = moment.tz(date, timezone).format('YYYY MMMM D, h:mm A z')

  return (
    <span
      style={{
        color: '#B4B3BB',
        display: 'inline-block',
        marginLeft: '8px',
        fontSize: '12px',
        textAlign: 'center',
      }}
    >
      {translate.phrases.placeholder('Last Updated: {{date}}', { date: formattedDate })}
    </span>
  )
}

export const NodeConsumableModal: React.FC<{
  onCloseModal?: () => void
  selectedNode?: TActiveNode
}> = ({ selectedNode }) => {
  // Use the useSelector hook
  const lures = serviceCenterStore.useSelector(serviceCenterStore.selectors.getLuresAsArray)

  const mapSelectedNode = serviceCenterStore.useSelector(
    serviceCenterStore.selectors.getSelectedEquipmentNode,
  )

  const insectsById = fieldAssetStore.useSelector((s) => s.insects) || {}
  // Use the useState hook
  const [isLoading, setIsLoading] = useState(false)
  // Declare the variables
  const trapConsumables = ['liner', 'coroplast']
  const connectionStatus = networkStore.useSelector(networkStore.selectors.getConnectionStatus)
  const timezone = useTimezoneForSelectedPropertyOrRegion()
  // User click from node card -> selectedNode
  // User click from status panel -> mapSelectedNode
  const node = (selectedNode || mapSelectedNode) as TActiveNode

  const {
    propertyId,
    nodeType,
    nodeIdentifier,
    name: nodeName,
    trap: { targetInsectId } = {},
  } = node as TActiveNode

  const [nodeConsumablesStatus, setNodeConsumablesStatus] = useState<NodeConsumablesStatus>({
    lureActiveStartDate: null,
    lureIdsActive: [],
    linerActiveStartDate: null,
    coroplastActiveStartDate: null,
    batteryActiveStartDate: null,
  })

  const { lastService } = node?.trap || {}

  const formatNodeConsumableStatus = (nodeConsumablesStatus: TNodeConsumablesStatus | TNodeTrap) => {
    const activeLures = nodeConsumablesStatus?.lures?.active

    let lureActiveStartDate = null

    if (activeLures) {
      const lures = Object.values(activeLures)

      if (lures.length) {
        lureActiveStartDate = lures[0].activeStart as string
      }
    }

    const linerActiveStartDate = nodeConsumablesStatus?.liner?.activeStart || null
    const coroplastActiveStartDate = nodeConsumablesStatus?.coroplast?.activeStart || null
    const batteryActiveStartDate = nodeConsumablesStatus?.battery?.activeStart || null

    const lureIdsActive = lureActiveStartDate
      ? Object.keys(nodeConsumablesStatus.lures.active as TNodeLures).map((lureId) => +lureId)
      : []

    setNodeConsumablesStatus({
      lureIdsActive,
      lureActiveStartDate,
      linerActiveStartDate,
      coroplastActiveStartDate,
      batteryActiveStartDate,
    })

    form.setFieldValue('lureIdsActive', lureIdsActive)
  }

  useEffect(() => {
    const nodeConsumablesStatus = (node as TActiveNode)?.trap

    if (!nodeConsumablesStatus) return

    formatNodeConsumableStatus(nodeConsumablesStatus)
  }, [node])

  useEffect(() => {
    // Fetch node consumable status when online then overwrite status state by latest status
    const fetchConsumableStatus = async () => {
      try {
        setIsLoading(true)

        const nodeConsumablesStatus = await serviceCenterNodeConsumablesStatusGet({
          propertyId,
          nodeIdentifier,
        })

        formatNodeConsumableStatus(nodeConsumablesStatus)
      } catch (error) {
        captureFrontendError(error)

        showNotification({
          type: 'error',
          message: translate.phrases.placeholder('Failed to load trap consumables.'),
        })
      } finally {
        setIsLoading(false)
      }
    }

    if (connectionStatus.connected) {
      fetchConsumableStatus()
    }
  }, [nodeIdentifier])

  const initialValues = useMemo(
    () => ({
      lureAction: null,
      linerAction: null,
      coroplastAction: null,
      batteryAction: null,
      lureIdsActive: nodeConsumablesStatus['lureIdsActive'] || [],
    }),
    [nodeConsumablesStatus],
  )

  const form = useForm<FormValues>({
    initialValues,
    validate: {
      lureIdsActive: () => {
        const {
          values: { lureAction, lureIdsActive },
        } = form

        if (
          lureAction &&
          [nodeConsumableActions.INSTALL, nodeConsumableActions.SWAP].includes(lureAction) &&
          isEmpty(lureIdsActive)
        ) {
          return translate.phrases.placeholder('Select lure type(s)')
        }
      },
    },
  })

  const hasFormChanged = form.isDirty()

  const handleBack = () => {
    // Close the node consumable modal
    AboveAllModal.close(MODAL_ID)
  }

  const handleSubmit = async () => {
    const { hasErrors } = form.validate()

    if (hasErrors) return

    setIsLoading(true)

    try {
      const {
        values: { lureAction, linerAction, coroplastAction, batteryAction, lureIdsActive },
      } = form

      const consumablesToUpdate: (keyof typeof ConsumableType)[] = []
      const consumablesToRemove: (keyof typeof ConsumableType)[] = []

      const actionMapping: Record<string, { action: string | null; consumableType: string }> = {
        lure: { action: lureAction, consumableType: ConsumableType.LURE },
        liner: { action: linerAction, consumableType: ConsumableType.LINER },
        coroplast: { action: coroplastAction, consumableType: ConsumableType.COROPLAST },
        battery: { action: batteryAction, consumableType: ConsumableType.BATTERY },
      }

      Object.values(actionMapping).forEach(({ action, consumableType }) => {
        if (action === nodeConsumableActions.INSTALL || action === nodeConsumableActions.SWAP) {
          consumablesToUpdate.push(consumableType as keyof typeof ConsumableType)
        } else if (action === nodeConsumableActions.REMOVE) {
          consumablesToRemove.push(consumableType as keyof typeof ConsumableType)
        }
      })

      const promises = []

      if (consumablesToUpdate.length) {
        promises.push(
          updateNodeConfig({
            propertyId,
            nodeType,
            nodeIdentifier,
            trap: {
              targetInsectId,
              consumableTypes: consumablesToUpdate,
              consumableOperationType: 'SET',
              lureIds: lureIdsActive,
            },
          }),
        )
      }

      if (consumablesToRemove.length) {
        promises.push(
          updateNodeConfig({
            nodeIdentifier,
            propertyId,
            nodeType,
            trap: {
              targetInsectId,
              consumableTypes: consumablesToRemove,
              consumableOperationType: 'REMOVE',
            },
          }),
        )
      }

      await Promise.all(promises)

      showNotification({
        type: 'success',
        message: translate.phrases.placeholder('Trap consumables have been successfully updated.'),
      })

      AboveAllModal.close(MODAL_ID)
    } catch (error) {
      captureFrontendError(error)

      showNotification({
        type: 'error',
        message: translate.phrases.placeholder('Failed to update trap consumables.'),
      })
    } finally {
      setIsLoading(false)
    }
  }

  const handleOnValuesChange = (values: FormValues) => {
    const { lureAction } = values

    if (lureAction && [nodeConsumableActions.INSTALL, nodeConsumableActions.SWAP].includes(lureAction)) {
      // if there only one lure type for the target insect, select it by default

      if (targetInsectId) {
        const filteredLureIds = lures
          .filter((lure) => lure.insectIds.includes(targetInsectId))
          .map(({ lureId }) => lureId)

        if (filteredLureIds.length === 1) values.lureIdsActive = filteredLureIds
      }

      form.validate()
    }
  }

  const handleOnClearSelection = (resetValues: Partial<FormValues>) => {
    const resetFormValues = {
      ...form.values,
      ...resetValues,
    }

    form.setValues(resetFormValues)

    // Mantine 8's setValues keeps the per-field dirty flags that getInputProps set, so a cleared field
    // would still count as dirty; dropping them makes isDirty compare against the initial values again
    form.setDirty({})

    handleOnValuesChange(resetFormValues)
  }

  const isLureTypesVisible = () => {
    return (
      nodeConsumablesStatus.lureActiveStartDate ||
      (form.values.lureAction === nodeConsumableActions.INSTALL &&
        form.values.lureAction !== initialValues.lureAction)
    )
  }

  return (
    <>
      {isLoading && <Loader size={25} style={{ position: 'fixed', top: 20, right: 20, zIndex: 100 }} />}

      <WideHeader
        title={translate.phrases.placeholder('Manage Trap Consumables')}
        onClose={handleBack}
        isSecondaryModal={true}
      />

      <div
        css={{
          borderRadius: 3,
          padding: 20,
        }}
      >
        <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h4 css={{ margin: 0, alignItems: 'center' }}>
            {nodeName}
            {targetInsectId && (
              <div css={{ margin: 0, fontSize: '14px', marginTop: '4px', fontWeight: 'normal' }}>
                {insectsById[targetInsectId].name}
              </div>
            )}
          </h4>

          <EditButton
            label={translate.phrases.placeholder('Edit Trap')}
            onClick={() => openTrapConfigurationModal(node as TActiveNode)}
          />
        </div>

        <Input.Wrapper id="lureAction" css={{ marginTop: 20 }} label={getTrapConsumableLabel('lure')}>
          <ConsumableUpdatedLabel date={lastService?.lureServicedLastOn} timezone={timezone} />
          <R.Group {...form.getInputProps('lureAction')}>
            <Group mt="xs" justify="space-between" style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 10 }}>
                {nodeConsumablesStatus.lureActiveStartDate ? (
                  <>
                    <Radio value={nodeConsumableActions.SWAP} label={translate.phrases.placeholder('Swap')} />
                    <Radio
                      value={nodeConsumableActions.REMOVE}
                      label={translate.phrases.placeholder('Remove')}
                    />
                  </>
                ) : (
                  <>
                    <Radio
                      value={nodeConsumableActions.INSTALL}
                      label={translate.phrases.placeholder('Install')}
                    />
                  </>
                )}
              </div>

              {form.isDirty('lureAction') && (
                <ClearButton
                  onClick={() =>
                    handleOnClearSelection({
                      lureAction: initialValues.lureAction,
                      lureIdsActive: initialValues.lureIdsActive,
                    })
                  }
                />
              )}
            </Group>
          </R.Group>
        </Input.Wrapper>

        {/* Lure input */}
        <Input.Wrapper
          id="lureIdsActive"
          css={{
            marginTop: 10,
            display: isLureTypesVisible() ? 'block' : 'none',
          }}
          error={form.errors.lureIdsActive}
        >
          <LuresMultiSelect
            {...form.getInputProps('lureIdsActive')}
            targetInsectId={targetInsectId}
            propertyId={propertyId}
            disabled={form.values.lureAction === initialValues.lureAction}
          />
        </Input.Wrapper>

        {/* Liner and Coroplast inputs */}
        {trapConsumables.map((trapConsumable) => {
          const consumableActionKey = `${trapConsumable}Action` as keyof FormValues

          return (
            <Input.Wrapper
              css={{ marginTop: 20 }}
              key={consumableActionKey}
              label={getTrapConsumableLabel(trapConsumable)}
            >
              <ConsumableUpdatedLabel
                date={
                  lastService?.[
                    `${trapConsumable}ServicedLastOn` as 'linerServicedLastOn' | 'coroplastServicedLastOn'
                  ]
                }
                timezone={timezone}
              />

              <R.Group {...form.getInputProps(consumableActionKey)}>
                <Group mt="xs" justify="space-between" style={{ display: 'flex', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 10 }}>
                    {nodeConsumablesStatus[
                      `${trapConsumable}ActiveStartDate` as
                        'linerActiveStartDate' | 'coroplastActiveStartDate'
                    ] ? (
                      <>
                        <Radio
                          value={nodeConsumableActions.SWAP}
                          label={translate.phrases.placeholder('Swap')}
                        />
                        <Radio
                          value={nodeConsumableActions.REMOVE}
                          label={translate.phrases.placeholder('Remove')}
                        />
                      </>
                    ) : (
                      <>
                        <Radio
                          value={nodeConsumableActions.INSTALL}
                          label={translate.phrases.placeholder('Install')}
                        />
                      </>
                    )}
                  </div>

                  {form.isDirty(`${consumableActionKey}`) && (
                    <ClearButton
                      onClick={() =>
                        handleOnClearSelection({
                          [consumableActionKey]: initialValues[consumableActionKey],
                        })
                      }
                    />
                  )}
                </Group>
              </R.Group>
            </Input.Wrapper>
          )
        })}
        {/* Battery input */}
        <Input.Wrapper css={{ marginTop: 20 }} id="batteryAction" label={getTrapConsumableLabel('battery')}>
          <ConsumableUpdatedLabel timezone={timezone} date={lastService?.batteryServicedLastOn} />

          <R.Group {...form.getInputProps('batteryAction')}>
            <Group
              mt="xs"
              justify="space-between"
              style={{ display: 'flex', alignItems: 'center', height: 40 }}
            >
              <div style={{ display: 'flex', gap: 10 }}>
                <Radio value={nodeConsumableActions.SWAP} label={translate.phrases.placeholder('Swap')} />
              </div>

              {form.isDirty('batteryAction') && (
                <ClearButton
                  onClick={() =>
                    handleOnClearSelection({
                      batteryAction: initialValues.batteryAction,
                    })
                  }
                />
              )}
            </Group>
          </R.Group>
        </Input.Wrapper>
      </div>

      <Footer
        onPrevious={handleBack}
        onNext={handleSubmit}
        nextButtonLabel={translate.phrases.placeholder('Submit')}
        disableNextButton={isLoading || !hasFormChanged}
      />
    </>
  )
}
